import { Link } from "react-router-dom";
import { Check, Minus, Info, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Motion";
import { COVERAGE_NOTE, MEDICAID_TEXT, RATES_PLACEHOLDER, GFE_TEXT, MEDICAID_AGREEMENT_NOTE } from "../../data/content";

const MATRIX = [
  { name: "Peer Support", medicaid: true },
  { name: "CDCA Counseling", medicaid: true },
  { name: "Life Coaching", medicaid: false },
];

const OptionCard = ({ num, title, children, dark, testid }) => (
  <div
    data-testid={testid}
    className={`group relative flex h-full flex-col rounded-[2rem] border p-7 transition-transform duration-500 hover:-translate-y-1 sm:p-10 ${
      dark ? "border-[#1B241E] bg-[#1B241E] text-[#F4F1EA]" : "border-[#E2DCD0] bg-[#FAF8F4] text-[#1E2621]"
    }`}
  >
    <span className={`font-mono-ui text-xs tracking-[0.2em] ${dark ? "text-[#E9A88F]" : "text-[#A9533A]"}`}>OPTION {num}</span>
    <h3 className="font-serif-display mt-4 text-3xl leading-tight sm:text-4xl">{title}</h3>
    <div className={`mt-5 flex-1 text-base leading-relaxed ${dark ? "text-[#DDE2DA]" : "text-[#4A554D]"}`}>{children}</div>
  </div>
);

const Matrix = () => (
  <div className="overflow-hidden rounded-[1.5rem] border border-[#E2DCD0] bg-[#FDFBF7]" data-testid="payment-matrix">
    <div className="grid grid-cols-[1.4fr_1fr_1fr] border-b border-[#E2DCD0] bg-[#F4F1EA] px-5 py-3 font-mono-ui text-[11px] uppercase tracking-[0.16em] text-[#4A554D] sm:px-8 sm:text-xs">
      <span>Service</span><span>Ohio Medicaid</span><span>Private pay</span>
    </div>
    {MATRIX.map((r) => (
      <div key={r.name} className="grid grid-cols-[1.4fr_1fr_1fr] items-center border-b border-[#E2DCD0] px-5 py-4 text-sm last:border-0 sm:px-8 sm:text-base">
        <span className="font-medium text-[#1E2621]">{r.name}</span>
        <span className="flex items-center gap-2 text-[#4A554D]">
          {r.medicaid ? <><Check className="h-4 w-4 shrink-0 text-[#5E7259]" /> May be covered*</> : <><Minus className="h-4 w-4 shrink-0 text-[#A9533A]" /> Not covered</>}
        </span>
        <span className="flex items-center gap-2 text-[#4A554D]">
          <Check className="h-4 w-4 shrink-0 text-[#5E7259]" /> {r.medicaid ? "Available" : "Private pay only"}
        </span>
      </div>
    ))}
  </div>
);

export const PayingForServices = () => (
  <section id="paying" className="scroll-mt-24 bg-[#FDFBF7] py-20 sm:py-28 lg:py-32" data-testid="paying-for-services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Payment &amp; access</Eyebrow>
          <h2 className="font-serif-display mt-4 text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="paying-for-services-heading">
            Paying for <span className="italic text-[#A9533A]">Services</span>
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <p className="text-base leading-relaxed text-[#4A554D] md:text-lg" data-testid="coverage-note">{COVERAGE_NOTE}</p>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-6 lg:mt-16 lg:grid-cols-2 lg:gap-8">
        <Reveal className="h-full">
          <OptionCard num="01" title="Ohio Medicaid" testid="payment-option-medicaid">
            <p>{MEDICAID_TEXT}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Peer Support", "CDCA Counseling"].map((s) => (
                <span key={s} className="rounded-full bg-[#E5EBE3] px-3.5 py-1.5 text-xs font-semibold text-[#2C3E33]">{s}</span>
              ))}
            </div>
          </OptionCard>
        </Reveal>
        <Reveal className="h-full" delay={0.12}>
          <OptionCard num="02" title="Private pay" testid="payment-option-private">
            <p>Private pay is available for all three services: Peer Support, CDCA Counseling and Life Coaching.</p>
            <p className="font-mono-ui mt-6 inline-block rounded-lg border border-dashed border-[#C86D51]/60 bg-[#FFF8F0] px-3 py-2 text-sm text-[#8A5416]" data-testid="rates-placeholder">
              {RATES_PLACEHOLDER}
            </p>
          </OptionCard>
        </Reveal>
      </div>

      <div className="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-12 lg:gap-8">
        <Reveal className="lg:col-span-8"><Matrix /></Reveal>
        <Reveal className="lg:col-span-4" delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-6 rounded-[1.5rem] border border-[#F3DBBA] bg-[#FFF8F0] p-7" data-testid="life-coaching-private-only-note">
            <Info className="h-6 w-6 text-[#8A5416]" />
            <p className="font-serif-display text-2xl leading-snug text-[#5C3A12]">
              Life Coaching is <span className="italic">private pay only.</span>
            </p>
            <p className="text-sm leading-relaxed text-[#6E4A1E]">*{COVERAGE_NOTE}</p>
          </div>
        </Reveal>
      </div>

      <div className="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-2 lg:gap-8">
        <Reveal className="h-full">
          <div className="flex h-full flex-col rounded-[1.5rem] border border-[#E2DCD0] bg-[#FAF8F4] p-7" data-testid="good-faith-estimate-note">
            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-[#A9533A]">Your right to know</p>
            <p className="font-serif-display mt-3 text-2xl leading-snug text-[#1E2621]">Good Faith Estimate</p>
            <p className="mt-3 text-sm leading-relaxed text-[#4A554D]">{GFE_TEXT}</p>
            <p className="mt-4 text-sm font-semibold text-[#1E2621]">Current rates: {RATES_PLACEHOLDER}</p>
            <Link to="/contact" className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#1E2621] px-5 py-2.5 text-sm font-medium text-[#F4F1EA] transition-colors hover:bg-[#A9533A]" data-testid="gfe-request-button">
              Request a Good Faith Estimate <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal className="h-full" delay={0.1}>
          <div className="flex h-full flex-col rounded-[1.5rem] border border-[#E2DCD0] bg-[#FAF8F4] p-7" data-testid="medicaid-agreement-note">
            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-[#A9533A]">For Ohio Medicaid members</p>
            <p className="mt-3 text-sm leading-relaxed text-[#4A554D]">{MEDICAID_AGREEMENT_NOTE}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#4A554D]">
              You also have rights about your information and your care. Read our{" "}
              <Link to="/privacy-practices" className="font-semibold text-[#A9533A] underline underline-offset-4" data-testid="paying-npp-link">Notice of Privacy Practices</Link>,{" "}
              <Link to="/client-rights" className="font-semibold text-[#A9533A] underline underline-offset-4" data-testid="paying-client-rights-link">Client Rights &amp; Grievance Procedure</Link>,{" "}
              <Link to="/nondiscrimination" className="font-semibold text-[#A9533A] underline underline-offset-4" data-testid="paying-nondiscrimination-link">Nondiscrimination Notice</Link> and{" "}
              <Link to="/language-help" className="font-semibold text-[#A9533A] underline underline-offset-4" data-testid="paying-language-link">free language help</Link>.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
